import React from "react";
import { cn } from "../../lib/utils";

const Tabs = ({ className, children, defaultValue, onValueChange, ...props }) => {
  const [value, setValue] = React.useState(defaultValue);

  const handleValueChange = (newValue) => {
    setValue(newValue);
    if (onValueChange) {
      onValueChange(newValue);
    }
  };

  const contextValue = {
    value,
    onValueChange: handleValueChange
  };

  return (
    <div className={cn("w-full", className)} data-tabs-value={value} {...props}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, contextValue);
        }
        return child;
      })}
    </div>
  );
};

const TabsList = ({ className, children, value, onValueChange, ...props }) => {
  return (
    <div
      className={cn(
        "inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground",
        className
      )}
      role="tablist"
      {...props}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, { value, onValueChange });
        }
        return child;
      })}
    </div>
  );
};

const TabsTrigger = ({ className, children, value: currentValue, onValueChange, triggerValue, ...props }) => {
  const isActive = currentValue === triggerValue;

  return (
    <button
      className={cn(
        "inline-flex items-center justify-center whitespace-nowrap rounded-xl px-3 py-2 text-sm font-semibold ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
        isActive
          ? "bg-gradient-to-r from-indigo-600 via-blue-500 to-cyan-400 text-white shadow-[0_12px_24px_rgba(59,130,246,0.28)]"
          : "text-slate-600 hover:bg-white/70 hover:text-slate-900",
        className
      )}
      onClick={() => onValueChange && onValueChange(triggerValue)}
      type="button"
      role="tab"
      aria-selected={isActive}
      {...props}
    >
      {children}
    </button>
  );
};

const TabsContent = ({ className, children, value: currentValue, contentValue, onValueChange, ...props }) => {
  if (currentValue !== contentValue) return null;

  // Filter out internal props before spreading to DOM
  const { value, onValueChange: _, ...domProps } = props;

  return (
    <div
      className={cn(
        "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        className
      )}
      role="tabpanel"
      {...domProps}
    >
      {children}
    </div>
  );
};

export { Tabs, TabsList, TabsTrigger, TabsContent };
